import { Button, Cell, FormControl, Grid, HeadingSection, HFlow, Icon, Tooltip, VFlow } from 'bold-ui'
import {
  CheckboxField,
  DateField,
  Form,
  FormRenderProps,
  MunicipioSelectField,
  SubmitButton,
  TextField,
} from 'components/form'
import { AldeiaSelectField } from 'components/form/field/select/AldeiaSelectField'
import { DseiSelectField } from 'components/form/field/select/DseiSelectField'
import { EtniaSelectField } from 'components/form/field/select/EtniaSelectField'
import { PoloBaseSelectField } from 'components/form/field/select/PoloBaseSelectField'
import { useServerTime } from 'hooks/useServerTime'
import { useMemo } from 'react'
import { metaPath } from 'util/metaPath'
import { createValidator, ErrorObject, maxLength } from 'util/validation'
import { InternalGovBrButton } from 'view/login/InternalGovBrButton'

import { useNeedLoginGovBrToCadsus } from '../../hooks/useNeedLoginGovBrToCadsus'
import { createCidadaoPesquisarCalculator } from '../calculator-cidadaoPesquisar'
import { default as CidadaoPesquisarFormModel } from '../types/model-pesquisarCidadao'

const path = metaPath<CidadaoPesquisarFormModel>()

export const validate = createValidator<CidadaoPesquisarFormModel>(
  {
    nomeCpfCns: maxLength[100],
    nomeMae: maxLength[100],
  },
  (values) => {
    const errors: ErrorObject<CidadaoPesquisarFormModel> = {}

    if (values && values.nomeCpfCns && Number(values.nomeCpfCns)) {
      if (values.nomeCpfCns.length !== 11 && values.nomeCpfCns.length !== 15) {
        return { nomeCpfCns: 'Informe um CPF ou CNS válido para realizar a busca' }
      }
    }

    return errors
  }
)

interface CidadaoPesquisarProps {
  handleSubmitPesquisa(form: CidadaoPesquisarFormModel): void
  initialValue?: CidadaoPesquisarFormModel
}

export function CidadaoAldeadoPesquisarForm(props: CidadaoPesquisarProps) {
  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  const decorators = useMemo(() => [createCidadaoPesquisarCalculator(path)], [])

  const { needLoginGovBr } = useNeedLoginGovBrToCadsus()

  const renderForm = (formProps: FormRenderProps<CidadaoPesquisarFormModel>) => {
    const clearFields = () => {
      formProps.form.change(path.nomeCpfCns.absolutePath(), null)
      formProps.form.change(path.dataNascimento.absolutePath(), null)
      formProps.form.change(path.nomeMae.absolutePath(), null)
      formProps.form.change(path.municipio.absolutePath(), null)
      formProps.form.change(path.cidadaoAldeado.naoIndigena.absolutePath(), null)
      formProps.form.change(path.cidadaoAldeado.etnia.absolutePath(), null)
      formProps.form.change(path.cidadaoAldeado.dsei.absolutePath(), null)
      formProps.form.change(path.cidadaoAldeado.poloBase.absolutePath(), null)
      formProps.form.change(path.cidadaoAldeado.aldeia.absolutePath(), null)
    }

    return (
      <form onSubmit={formProps.handleSubmit} noValidate>
        <VFlow>
          <Grid>
            <Cell xs={12}>
              <TextField
                uppercase
                name={path.nomeCpfCns}
                label='Nome/CNS/CPF'
                maxLength={200}
                style={{ fontSize: '1.5rem' }}
              />
            </Cell>
            <Cell sm={2} xs={12}>
              <DateField name={path.dataNascimento} maxDate={now} label='Data de nascimento' />
            </Cell>
            <Cell sm={5} xs={12}>
              <TextField uppercase name={path.nomeMae} label='Nome da mãe' />
            </Cell>
            <Cell sm={5} xs={12}>
              <MunicipioSelectField name={path.municipio} label='Município de nascimento' />
            </Cell>
            <Cell size={12}>
              <FormControl label='Etnia'>
                <Grid>
                  <Cell sm={4} xs={8}>
                    <Tooltip text={formProps.values.cidadaoAldeado?.naoIndigena && 'Cidadão não indígena'}>
                      <EtniaSelectField
                        name={path.cidadaoAldeado.etnia}
                        disabled={formProps.values.cidadaoAldeado?.naoIndigena}
                      />
                    </Tooltip>
                  </Cell>
                  <Cell sm={4}>
                    <CheckboxField name={path.cidadaoAldeado.naoIndigena} label='Não indígena' />
                  </Cell>
                </Grid>
              </FormControl>
            </Cell>
            <Cell size={12}>
              <HeadingSection level={3} title='Residência'>
                <Grid>
                  <Cell sm={4} xs={12}>
                    <DseiSelectField
                      name={path.cidadaoAldeado.dsei}
                      label='DSEI'
                      poloBaseId={formProps.values.cidadaoAldeado?.poloBase?.id}
                      aldeiaId={formProps.values.cidadaoAldeado?.aldeia?.id}
                    />
                  </Cell>
                  <Cell sm={4} xs={12}>
                    <PoloBaseSelectField
                      name={path.cidadaoAldeado.poloBase}
                      label='Polo Base'
                      dseiId={formProps.values.cidadaoAldeado?.dsei?.id}
                      aldeiaId={formProps.values.cidadaoAldeado?.aldeia?.id}
                    />
                  </Cell>
                  <Cell sm={4} xs={12}>
                    <AldeiaSelectField
                      name={path.cidadaoAldeado.aldeia}
                      label='Aldeia'
                      dseiId={formProps.values.cidadaoAldeado?.dsei?.id}
                      poloBaseId={formProps.values.cidadaoAldeado?.poloBase?.id}
                    />
                  </Cell>
                </Grid>
              </HeadingSection>
            </Cell>
          </Grid>
          <HFlow justifyContent='flex-end' style={{ paddingBottom: '15px' }}>
            {needLoginGovBr && (
              <Tooltip text='Para acessar os serviços do CADSUS, é necessário estar logado no gov.br.'>
                <HFlow justifyContent='center'>
                  <InternalGovBrButton href='/oauth2/authorization/govbr' />
                </HFlow>
              </Tooltip>
            )}
            <Button kind='normal' skin='outline' size='medium' onClick={clearFields}>
              Limpar filtros
            </Button>
            <SubmitButton
              kind='primary'
              skin='default'
              size='medium'
              handleSubmit={formProps.handleSubmit}
              data-cy='BuscarCidadaoButton'
            >
              <Icon icon='zoomOutline' style={{ marginRight: '0.5rem' }} />
              Buscar cadastro
            </SubmitButton>
          </HFlow>
        </VFlow>
        <input type='submit' hidden />
      </form>
    )
  }

  return (
    <Form<CidadaoPesquisarFormModel>
      render={renderForm}
      validate={validate}
      initialValues={props.initialValue}
      onSubmit={props.handleSubmitPesquisa}
      decorators={decorators}
    />
  )
}
